import { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/shared/ui/theme/ThemeProvider';
import { useProfile } from '@/modules/profiles/presentation/context/ProfileContext';
import { EmployeeDetailHeader } from '@/presentation/components/EmployeeDetailHeader';
import { DateRangeFilter } from '@/presentation/components/DateRangeFilter';
import { useTimelogsByAccessRoleQuery } from '@/modules/attendance/application/timelogQueries';
import { useEmployeeByEntityQuery } from '@/modules/employees/application/employeeQueries';
import type { Timelog } from '@/modules/attendance/domain/entities/Timelog';
import type { AttendanceEmployee } from '@/modules/attendance/infrastructure/mappers/timelogMapper';
import { TimelogListItem } from '../components/TimelogListItem';
import { TimelogDetailModal } from '../components/TimelogDetailModal';
import { GlassLoader } from '@/shared/ui/components/GlassLoader';
import { defaultRange } from '@/shared/utils/datetime';

export function EmployeePortalPage() {
  const { t } = useTranslation('common');
  const { C, T } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedRole } = useProfile();

  // location.state provides identity for instant render (initials, name, avatarColor)
  const employee = (location.state as { employee?: AttendanceEmployee } | null)?.employee ?? null;

  // Live query — provides cargo/dept (not available in AttendanceEmployee).
  // Guard: only enabled when idEntity is present and > 0 (same pattern as admin page).
  const employeeQuery = useEmployeeByEntityQuery(employee?.idEntity ?? null);
  const cargo = employeeQuery.data?.cargo ?? null;
  const dept  = employeeQuery.data?.dept  ?? null;

  const __dr = defaultRange();
  const [from, setFrom] = useState(__dr.from);
  const [to,   setTo  ] = useState(__dr.to);
  const [detail, setDetail] = useState<Timelog | null>(null);

  const { data: allTimelogs = [], isFetching } = useTimelogsByAccessRoleQuery(
    selectedRole?.idAccessRole ?? null,
    from,
    to,
  );

  const filtered = useMemo(() => {
    if (!employee) return allTimelogs;
    return allTimelogs.filter((tl) => tl.idPerson === employee.idPerson);
  }, [allTimelogs, employee]);

  return (
    <div style={{ flex: 1, background: T.bg, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Shared header — prototype-faithful centered layout (Clock Web.html lines 801-815) */}
      <EmployeeDetailHeader
        initials={employee?.initials ?? ''}
        avatarColor={employee?.avatarColor ?? C.primary}
        photoUrl={employeeQuery.data?.photoUrl ?? employee?.photoUrl ?? null}
        fullName={employee?.fullName ?? t('employee.fallback')}
        cargo={cargo}
        dept={dept}
        onBack={() => { void navigate('/'); }}
        title={t('employee.misMarkajesTitle')}
        headerTestId="portal-employee-header"
        titleTestId="portal-employee-detail-title"
        cargoTestId="portal-employee-cargo"
        deptTestId="portal-employee-dept"
        T={T}
        C={C}
      />

      {/* Scrollable content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '21px 30px 30px' }}>
        <div style={{ maxWidth: 645, margin: '0 auto' }}>
          {/* Spacing wrapper — prototype gap between filter bar and timelog list (Clock_Web.html).
              Per design.md P2: per-page wrapper only; shared DateRangeFilter is also used in
              AdminMarcajesPage with a different layout context, so no shared-component prop. */}
          <div data-testid="portal-filter-wrapper" style={{ marginBottom: 18 }}>
            <DateRangeFilter
              value={{ from, to }}
              onChange={({ from: newFrom, to: newTo }) => {
                setFrom(newFrom);
                setTo(newTo);
              }}
              label={t('employee.timelogTitle')}
            />
          </div>

          {/* Timelog list */}
          <div style={{ position: 'relative', overflow: 'hidden' }}>
            {filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '45px 0', color: T.textMuted, fontSize: 11 }}>
                {t('employee.noRecords')}
              </div>
            ) : (
              filtered.map((tl) => (
                <TimelogListItem
                  key={tl.idTimelog}
                  timelog={tl}
                  initials={employee?.initials ?? '??'}
                  avatarColor={employee?.avatarColor ?? C.primary}
                  onClick={() => { setDetail(tl); }}
                  T={T}
                  C={C}
                />
              ))
            )}
            {isFetching && <GlassLoader T={T} />}
          </div>
        </div>
      </div>

      {/* Detail modal */}
      {detail && employee && (
        <TimelogDetailModal
          timelog={detail}
          initials={employee.initials}
          avatarColor={employee.avatarColor}
          onClose={() => { setDetail(null); }}
          T={T}
          C={C}
        />
      )}
    </div>
  );
}
